import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router";
import {
  X,
  Mail,
  Phone,
  Building2,
  BadgeCheck,
  MapPin,
  UserCircle2,
  CalendarDays,
  IdCard,
  Clock,
  ShieldCheck,
  ArrowUpRight,
  Copy,
  ExternalLink,
  Power,
  ChevronRight,
  Briefcase,
  Monitor,
  Search,
  StickyNote,
  FileText,
  User,
  Globe,
  Quote,
  Building,
  Calendar,
  Key,
  Award,
  BookOpen,
  CheckCircle,
  Palette,
  Bell,
  Users,
  Ban,
  LockKeyhole,
  Lock,
  Image,
  Map,
  Globe2,
  Layers,
  MessageSquare,
  Box,
  MailCheck,
  PhoneCall,
  ThumbsDown,
  Check,
  Trophy,
  XCircle,
  Pencil,
  ThumbsUp,
} from "lucide-react";
import { useApiCall } from "../../../../../component/custom-hooks/api-call";
import InnerLoader from "../../../../../component/ui/loader/inner-loader";
import WonDeal from "../won-deal/won-form-deal";
import EditDealModal from "../../../../../component/modals/add-edit-modal/EditDealModal";
import Swal from "sweetalert2";
import CommentsSection from "../../../../../component/comment";
import LostDeal from "../lost-deal/lost-form-deal";
import NewCardIcon from "../../../../../component/icons/action-card-icon";

const tabs = [
  { key: "summary", label: "Summary" },
  { key: "customer_information", label: "About" },
  { key: "stages", label: "Stages" },

  { key: "call", label: "Calls" },
  { key: "emails", label: "Emails" },
  { key: "sms", label: "Sms" },
  { key: "comments", label: "Comments" },
];

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

// function useOnClickOutside(ref, handler) {
//   useEffect(() => {
//     function listener(event) {
//       if (!ref.current || ref.current.contains(event.target)) return;
//       handler(event);
//     }
//     document.addEventListener("mousedown", listener);
//     document.addEventListener("touchstart", listener);
//     return () => {
//       document.removeEventListener("mousedown", listener);
//       document.removeEventListener("touchstart", listener);
//     };
//   }, [ref, handler]);
// }

function Badge({ children, tone = "default", icon: Icon }) {
  const tones = {
    default: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-rose-100 text-rose-700",
    grey: "bg-neutral-100 text-neutral-700",
    gray: "bg-gray-100 text-gray-800",
    yellow: "bg-yellow-100 text-yellow-700",
    orange: "bg-orange-100 text-orange-700",
    violet: "bg-violet-100 text-violet-700",
    neutral: "bg-gray-50 text-gray-700 border border-gray-200",
    pink: "bg-pink-200 text-pink-700",
    teal: "bg-teal-200 text-teal-700",
    indigo: "bg-indigo-200 text-indigo-700",
    sky: "bg-sky-200 text-sky-700",
    lime: "bg-lime-200 text-lime-700",
  };
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
        tones[tone],
      )}
    >
      {Icon ? <Icon size={14} /> : null}
      {children}
    </span>
  );
}

function Stat({ title, value, icon: Icon, hint, tone = "default" }) {
  return (
    <div className="group relative rounded-2xl border border-solid border-slate-200 bg-white p-4 shadow-sm hover:shadow-md">
      <div className="flex items-start gap-3">
        {/* <div className="rounded-xl bg-slate-50 p-2">
          <Icon className="size-5" />
        </div> */}
        <div className="min-w-0">
          <div className="text-xs text-slate-500 capitalize">
            {title.includes("_")
              ? title.split("_").map((word) => ` ${word}`)
              : title}
          </div>
          {/* truncate */}
          <div className="text-lg font-semibold tracking-tight">{value}</div>
          {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
        </div>
      </div>
      {/* {tone !== "default" && (
        <div className="absolute right-3 top-3">
          <Badge tone={tone}>{tone === "green" ? "Good" : tone === "amber" ? "Watch" : "Check"}</Badge>
        </div>
      )} */}
    </div>
  );
}
function Field({ label, value, icon: Icon, copyable, link }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="grid grid-cols-12 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50">
      <div className="col-span-4 flex items-center gap-2 text-[13px] font-medium text-slate-500">
        {Icon ? <Icon className="size-4" /> : null}
        {label}
      </div>
      <div className="col-span-8 flex items-center justify-between gap-2 text-[13px] text-slate-800">
        {link ? (
          <a
            href={link}
            className="truncate underline decoration-slate-300 underline-offset-2 hover:text-blue-600 hover:decoration-blue-400"
          >
            {value}
          </a>
        ) : (
          <span className="">{value}</span>
        )}
        {copyable && (
          <button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(String(value ?? ""));
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              } catch {}
            }}
            className="inline-flex items-center gap-1 rounded-md border border-solid border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100"
          >
            <Copy className="size-3.5" /> {copied ? "Copied" : "Copy"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function DealQuickView({
  open = true,
  onClose = () => {},
  data,
  DealStages = [],
}) {
  const [deal, setdeal] = useState({});
  const [activeTab, setActiveTab] = useState("summary");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(deal?.dealstatus);
  const containerRef = useRef(null);
  const apiCall = useApiCall();
  const [isLoading, setIsLoading] = useState(false);

  const [contact, setcontact] = useState({});
  const [alertData, setAlertData] = useState({ isShow: false });
  const [changeModal, setChangeModal] = useState(false);

  // const GetData = async (id) => {
  //   setIsLoading(true);
  //   let apiData = JSON.stringify({
  //     "dealid": id
  //   });

  //   try {
  //     let call = await apiCall({ method: 'get', url: `deals/get-deal/${id}`, data: apiData });
  //     let dealData = call?.data

  //     // console.log("dealData", dealData)

  //     let activity = (dealData?.recent_activity)?.map(item => {
  //       const dateObj = new Date(item.time);
  //       const formattedDate = dateObj.toISOString().slice(0, 16).replace("T", " ");
  //       // This gives "YYYY-MM-DD HH:MM"

  //       return {
  //         ts: formattedDate,
  //         text: item.data
  //       };
  //     });

  //     setdeal(sdata);
  //     setStatus(dealData?.current_stage?.dealstatus);
  //     setIsLoading(false);
  //   } catch (error) {
  //     console.warn("Error : ", error);
  //     setIsLoading(false);
  //   }
  // };

  const GetData = async (id) => {
    let apiData = JSON.stringify({
      userid: id,
    });

    try {
      let call = await apiCall({
        method: "get",
        url: `deals/get-deal/${id}`,
        data: apiData,
      });
      let dealData = call?.data;

      console.log("ContactData", dealData);
      setIsLoading(true);

      let activity = dealData?.recent_activity?.map((item) => {
        const dateObj = new Date(item.time);
        const formattedDate = dateObj
          .toISOString()
          .slice(0, 16)
          .replace("T", " ");
        // This gives "YYYY-MM-DD HH:MM"

        return {
          ts: formattedDate,
          text: item.data,
        };
      });

      const sdata = {
        // ---------- DEAL SUMMARY ----------
        dealid: dealData?.dealid,
        name: dealData?.name,
        product: dealData?.deal_product_name,
        package: dealData?.deal_package_name,
        dealkey: dealData?.dealkey,
        dealstatus: dealData?.current_stage?.dealstatus,
        is_closed: dealData?.is_closed ? "Yes" : "No",
        dealtime: dealData?.dealtime * 1000,
        created_at: dealData?.created_at,
        product: dealData?.product,

        // ---------- deal INFO ----------
        contactid: dealData?.contact?.userid,
        deal_name: dealData?.contact?.name,
        deal_email: dealData?.contact?.email,
        deal_phone: dealData?.contact?.phone,
        deal_source: dealData?.contact?.source,
        deal_page_source: dealData?.contact?.page_source,
        deal_company: dealData?.contact?.companyname,
        deal_signupdate: dealData?.contact?.signupdate,
        country: dealData?.contact?.country,
        stats: dealData?.stats,
        updated_at: dealData?.updated_at,

        // ---------- EXTRA ----------
        comments: dealData?.comments,
        stages: dealData?.stages,
        current_stage: dealData?.current_stage,
        metas: dealData?.metas,
        activity,
      };

      setStatus(dealData?.current_stage?.dealstatus);
      setdeal(sdata);
      setIsLoading(true);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };

  // useOnClickOutside(containerRef, () => onClose());

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key.toLowerCase() === "f" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const el = document.getElementById("quickview-search");
        el?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onSuccess = (message) => {
    setAlertData({ isShow: true, type: "success", message: message });
    GetData(contact?.id);
    setChangeModal(false);
  };

  const onError = (message) => {
    setAlertData({ isShow: true, type: "error", message: message });
  };

  // console.log("deal in fields", deal);

  const fields = useMemo(
    () => [
      // ---------- DEAL SUMMARY ----------
      { tab: "summary", label: "ID", value: deal?.dealid, icon: IdCard },
      {
        tab: "summary",
        label: "Name",
        value: deal?.deal_name,
        icon: Briefcase,
      },
      {
        tab: "summary",
        label: "Product",
        value: deal?.product?.products,
        icon: Layers,
      },
      { tab: "summary", label: "Package", value: deal?.package, icon: Box },
      { tab: "summary", label: "Status", value: status, icon: CheckCircle },
      // { tab: "summary", label: "Is Closed", value: deal?.is_closed, icon: Lock },
      // { tab: "summary", label: "Deal Time", value: deal?.dealtime, icon: Clock },
      {
        tab: "summary",
        label: "Date",
        value: deal?.created_at,
        icon: CalendarDays,
      },

      // ---------- deal INFO ----------
      // { tab: "customer_information", label: "Name", value: deal?.deal_name, icon: User },
      {
        tab: "customer_information",
        label: "Email",
        value: deal?.deal_email,
        icon: Mail,
        link: `mailto:${deal?.deal_email}`,
        copyable: true,
      },
      {
        tab: "customer_information",
        label: "Phone",
        value: deal?.deal_phone,
        icon: Phone,
        link: `tel:${deal?.deal_phone}`,
        copyable: true,
      },
      {
        tab: "customer_information",
        label: "Country",
        value: deal?.country,
        icon: ExternalLink,
      },
      {
        tab: "customer_information",
        label: "Brand",
        value: deal?.deal_page_source,
        icon: Globe,
      },
      {
        tab: "customer_information",
        label: "Company",
        value: deal?.deal_company,
        icon: Building,
      },
      // { tab: "customer_information", label: "Signup Date", value: deal?.deal_signupdate, icon: Calendar },
    ],
    [deal, status],
  );

  const filteredFields = fields.filter(
    (f) =>
      (!query ||
        `${f.label} ${f.value}`.toLowerCase().includes(query.toLowerCase())) &&
      (activeTab === "summary"
        ? ["summary", "work", "personal"].includes(f.tab)
        : f.tab === activeTab),
  );

  useEffect(() => {
    GetData(data?.dealid);
  }, [data]);

  // console.log(data);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-stretch justify-end bg-slate-900/40 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.aside
            ref={containerRef}
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            className="relative mt-auto mr-[2vh] mb-auto ml-0 flex h-[96vh] w-[750px] max-w-[98vw] flex-col overflow-hidden rounded-3xl border border-solid border-slate-200 bg-gradient-to-b from-white to-slate-50 shadow-xl"
            aria-modal
            role="dialog"
          >
            {isLoading ? (
              <>
                {/* Header */}
                <div className="border-b border-solid border-slate-200 bg-white/90 p-4 backdrop-blur supports-[backdrop-filter]:bg-white/70 w-full">
                  <div className="flex flex-wrap items-center justify-between gap-3 w-full">
                    <div className="flex min-w-0 items-center gap-3">
                      {/* <img
                          src={deal?.userimg}
                          alt={deal?.name}
                          className="size-12 rounded-2xl object-cover ring-2 ring-white shadow"
                        /> */}
                      <div className="flex items-center justify-center size-12 bg-gradient-to-br from-indigo-500/20 to-emerald-500/20  rounded-full overflow-hidden">
                        <p className="capitalize font-semi-bold text-base text-black">
                          {deal?.name?.split(" ")?.map((n, i) => n.charAt(0))}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h2 className="truncate text-lg font-semibold tracking-tight text-slate-900">
                            {deal?.name}
                          </h2>
                          <Badge
                            tone={
                              status == "Need Analysis"
                                ? "yellow"
                                : status == "Value Proposition"
                                  ? "blue"
                                  : status == "Identify Decision"
                                    ? "violet"
                                    : status == "Proposal"
                                      ? "teal"
                                      : status == "Review"
                                        ? "indigo"
                                        : status == "Won"
                                          ? "green"
                                          : status == "Lost"
                                            ? "red"
                                            : "gray"
                            }
                            icon={BadgeCheck}
                          >
                            {status || "-"}
                          </Badge>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                          <span className="inline-flex items-center gap-1">
                            <MailCheck className="size-4" /> {deal?.deal_email}
                          </span>

                          <span className="inline-flex items-center gap-1">
                            <PhoneCall className="size-4" /> {deal?.deal_phone}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/lead-account/detail/${deal?.contactid}/deal/${deal?.dealid}`}
                        target="_blank"
                        className="inline-flex items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      >
                        <NewCardIcon className="h-4.5 w-4.5 text-[#34C759]" />{" "}
                        View Full Details
                      </Link>
                      <button
                        className="inline-flex items-center gap-1.5 rounded-xl bg-[#0B54FF] px-3 py-2 text-sm font-medium text-white hover:bg-[#0849e0]"
                        onClick={onClose}
                        aria-label="Close"
                      >
                        <X className="size-4" /> Exit
                      </button>
                    </div>
                  </div>

                  {/* Quick actions */}
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <QuickAction
                      icon={Mail}
                      label="Email"
                      href={`mailto:${deal?.deal_email}`}
                    />
                    <QuickAction
                      icon={Phone}
                      label="Call"
                      href={`tel:${deal?.deal_phone}`}
                    />
                    <div className="ml-auto flex items-center gap-2">
                      <kbd className="rounded-md border border-solid bg-white px-1.5 py-1 text-[10px] text-slate-500">
                        ⌘/Ctrl
                      </kbd>
                      <span className="text-[10px] text-slate-500">
                        + F to search
                      </span>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden p-4 w-full">
                  {/* Left: main content */}
                  <div className="col-span-12 flex min-h-0 flex-col gap-4">
                    {/* Top stats */}
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                      {deal?.stats?.map((ele, idx) => {
                        return <Stat title={ele?.key} value={ele?.value} />;
                      })}
                    </div>

                    {/* Tabs */}
                    <div className="flex justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-1">
                        {tabs?.map((t) => (
                          <button
                            key={t.key}
                            onClick={() => setActiveTab(t.key)}
                            className={classNames(
                              "rounded-md px-[12px] py-[8px] text-sm font-medium border border-solid border-[#E1E4EA]",
                              activeTab === t.key
                                ? "bg-[#F5F5F5] text-[#0E121B]"
                                : "text-slate-600 hover:bg-slate-100",
                            )}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                      <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-2.5 size-4 text-slate-400" />
                        <input
                          id="quickview-search"
                          placeholder="Search fields…"
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          className="w-64 rounded-2xl border border-solid border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none placeholder:text-slate-400 focus:ring-2 focus:ring-slate-900/10"
                        />
                      </div>
                    </div>

                    <div className="h-full overflow-auto rounded-2xl py-2">
                      {/* Render fields for current tab (or summary) */}
                      <div className="grid grid-cols-1 gap-3">
                        {filteredFields?.map((f, idx) => (
                          <Field key={idx} {...f} />
                        ))}
                      </div>

                      {activeTab === "notes" && <Notes />}
                      {activeTab === "assets" && (
                        <Assets assets={deal?.assets} />
                      )}
                      {activeTab === "activity" && (
                        <Activity items={deal?.activity} />
                      )}

                      {activeTab === "stages" && (
                        <div className="space-y-3">
                          <DealCard
                            deal={deal}
                            onSubmit={onSuccess}
                            DealStages={DealStages}
                            onError={onError}
                            onStatusChange={(dealId, newStatus) => {
                              setdeal((prev) => {
                                return {
                                  ...prev,
                                  current_stage: {
                                    ...prev?.current_stage,
                                    dealstatus: newStatus,
                                  },
                                };
                              });
                            }}
                          />
                        </div>
                      )}

                      {activeTab === "comments" && (
                        <CommentsSection
                          type="deal"
                          id={deal?.dealid}
                          apiBaseUrl="deal"
                          setAlertData={setAlertData}
                        />
                      )}

                      {activeTab === "call" && (
                        <TabPlaceholder
                          icon={PhoneCall}
                          title="Calls"
                          message="We are working on the call logs and recordings section."
                        />
                      )}

                      {activeTab === "emails" && (
                        <TabPlaceholder
                          icon={Mail}
                          title="Emails"
                          message="Email activity and history will be available soon."
                        />
                      )}

                      {activeTab === "sms" && (
                        <TabPlaceholder
                          icon={MessageSquare}
                          title="SMS"
                          message="SMS communication history is under development."
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-2 border-t border-solid border-slate-200 bg-white p-3 w-full">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <IdCard className="size-4" /> ID {deal?.dealid}
                  </div>
                </div>
              </>
            ) : (
              <InnerLoader wrapperClass={"w-full"} />
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function QuickAction({ icon: Icon, label, href, onClick }) {
  const className =
    "inline-flex items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50";
  if (href)
    return (
      <a className={className} href={href}>
        <Icon className="size-4" /> {label}
      </a>
    );
  return (
    <button className={className} onClick={onClick}>
      <Icon className="size-4" /> {label}
    </button>
  );
}

function Activity({ items, compact = false }) {
  return (
    <ul className="space-y-3">
      {items?.map((a, i) => (
        <li key={i} className="flex items-start gap-3">
          <div className="mt-1 h-5 w-5 rounded-full bg-slate-100 ring-1 ring-slate-200" />
          <div className="min-w-0 flex-1">
            <div className="text-[13px] text-slate-800 pb-[5px]">{a.text}</div>
            <div className="text-[11px] text-slate-400">{formatTs(a.ts)}</div>
          </div>
        </li>
      ))}
      {!compact && (
        <button className="mt-1 inline-flex items-center gap-1 text-xs text-slate-600 hover:underline">
          View full activity <ChevronRight className="size-4" />
        </button>
      )}
    </ul>
  );
}

function Assets({ assets }) {
  return (
    <div className="space-y-3">
      {assets?.map((a, i) => (
        <div
          key={i}
          className="flex items-center justify-between rounded-xl border border-solid border-slate-200 bg-white p-3"
        >
          <div className="flex min-w-0 items-center gap-3">
            <Monitor className="size-5" />
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{a.name}</div>
              <div className="truncate text-xs text-slate-500">{a.id}</div>
            </div>
          </div>
          <Badge tone={a.status === "Assigned" ? "blue" : "grey"}>
            {a.status}
          </Badge>
        </div>
      ))}
      <button className="inline-flex items-center gap-1 text-xs text-slate-600 hover:underline">
        Manage assets <ChevronRight className="size-4" />
      </button>
    </div>
  );
}

function Notes() {
  const [notes, setNotes] = useState([
    {
      id: 1,
      text: "Great initiative on latest branding sprint. Keep momentum for Q4.",
      ts: Date.now() - 86400000,
    },
  ]);
  const [text, setText] = useState("");
  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-solid border-slate-200 bg-white p-3">
        <textarea
          id="addNotesField"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a manager note (visible to HR/Managers)…"
          className="min-h-[90px] w-full resize-y rounded-xl border border-solid border-slate-200 bg-slate-50 p-3 text-sm outline-none focus:ring-2 focus:ring-slate-900/10"
        />
        <div className="mt-2 flex justify-end">
          <button
            onClick={() => {
              if (!text.trim()) return;
              setNotes((n) => [{ id: Date.now(), text, ts: Date.now() }, ...n]);
              setText("");
            }}
            className="rounded-xl bg-slate-900 px-3 py-1.5 text-xs text-white hover:bg-slate-800"
          >
            Save Note
          </button>
        </div>
      </div>
      <ul className="space-y-2">
        {notes?.map((n) => (
          <li
            key={n.id}
            className="rounded-xl border border-solid border-slate-200 bg-white p-3"
          >
            <div className="text-sm text-slate-800">{n.text}</div>
            <div className="text-[11px] text-slate-400">
              {new Date(n.ts).toLocaleString()}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl border border-solid border-slate-200 bg-slate-50 p-3 text-center">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-800">{value}</div>
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatTs(ts) {
  try {
    const d =
      typeof ts === "string" ? new Date(ts.replace(" ", "T")) : new Date(ts);
    return d.toLocaleString();
  } catch {
    return String(ts);
  }
}

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function DealCard({
  deal,
  onStatusChange,
  DealStages = [],
  onSubmit = () => {},
  onError = () => {},
}) {
  const [showWonModal, setShowWonModal] = useState(false);
  const [wonFormDeal, setWonFormDeal] = useState(null);

  const [showLostModal, setShowLostModal] = useState(false);
  const [lostFormDeal, setLostFormDeal] = useState(null);

  const [localStage, setLocalStage] = useState(
    deal?.current_stage?.dealstatus ?? "-",
  );

  const apiCall = useApiCall();

  const handleSuccess = (message) => {
    setWonFormDeal(false);
    setShowLostModal(false);
    onSubmit(message);
  };

  const handleError = (message) => {
    if (onError) onError(message);
  };

  console.log("deal", deal);

  console.log("DealStages at render:", DealStages);
  console.log("deal.current_stage.dealstatus:", deal.current_stage.dealstatus);

  const isWon = deal?.current_stage?.dealstatus === "Won";
  const isLost = deal?.current_stage?.dealstatus === "Lost";
  const currentStageIndex = DealStages.findIndex(
    (s) => s.name === deal?.current_stage?.dealstatus,
  );
  // console.log(currentStageIndex)
  const activeColor = isWon ? "#22c55e" : isLost ? "#ef4444" : "#2563eb";
  const isWonOrLost = isWon || isLost;

  const formatStageKey = (stage) => stage.toLowerCase().replace(/\s+/g, "_");

  // --- HANDLE STAGE CHANGE ---
  const handleStageChange = async (newStage) => {
    const lowerStage = formatStageKey(newStage);
    const currentStage = deal?.current_stage?.dealstatus ?? "-";

    if (lowerStage === "won") {
      setWonFormDeal(deal);
      setShowWonModal(true);
      return;
    }

    if (lowerStage === "lost") {
      setLostFormDeal({ dealkey: deal.dealkey, name: deal.name });
      setShowLostModal(true);
      return;
    }

    const resultSwal = await Swal.fire({
      title: `Move deal "${deal.name}"?`,
      text: `Move from "${currentStage}" to "${newStage}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      reverseButtons: true,
    });

    if (resultSwal.isConfirmed) {
      try {
        await apiCall({
          method: "post",
          url: "deals/update-deal-status",
          data: { dealkey: deal.dealkey, dealstatus: lowerStage },
        });

        onStatusChange(deal.dealid, newStage);
        Swal.fire("Success!", `Deal moved to "${newStage}"`, "success");
      } catch (err) {
        Swal.fire(
          "Error",
          err?.response?.data?.message || "Something went wrong",
          "error",
        );
      }
    }
  };

  const metas = Object.fromEntries(
    deal?.metas?.map((m) => [m.meta_key, m.meta_value]) ?? [],
  );

  return (
    <>
      <div className="relative rounded-xl   px-5 py-4  space-y-3 text-sm w-full">
        {/* Stage Pipeline */}
        <div className="mt-2">
          <p className="text-xs text-slate-500 mb-1 font-medium">
            Stage Pipeline
          </p>
          <div className="flex w-full h-7 overflow-hidden rounded-md">
            {DealStages.map((s, i) => {
              const isActive = !isWonOrLost && i <= currentStageIndex;
              const isFirst = i === 0;
              const isLast = i === DealStages.length - 1;

              let clipPath = "";
              if (isFirst)
                clipPath =
                  "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)";
              else if (isLast)
                clipPath = "polygon(0 0, 10px 50%, 0 100%, 100% 100%, 100% 0)";
              else
                clipPath =
                  "polygon(0 0, 10px 50%, 0 100%, calc(100% - 10px) 100%, 100% 50%, calc(100% - 10px) 0)";

              return (
                <div
                  key={s.id}
                  onClick={() => !isWonOrLost && handleStageChange(s.name)}
                  title={s.name}
                  style={{
                    clipPath,
                    WebkitClipPath: clipPath,
                    backgroundColor:
                      i <= currentStageIndex && !isWonOrLost
                        ? activeColor
                        : "#e5e7eb",
                    transition: "background-color 0.3s ease",
                    cursor: isWonOrLost ? "not-allowed" : "pointer",
                  }}
                  className="flex-1 relative flex items-center justify-center text-[10px] font-medium text-white select-none"
                >
                  {i <= currentStageIndex && !isWonOrLost ? (
                    <Check className="size-3" />
                  ) : (
                    s.name[0]
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions Row */}
        <div className="flex justify-between items-center mt-4">
          {/* Won/Lost Badge */}
          <div>
            {(isWon || isLost) && (
              <div
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                  isWon
                    ? "bg-green-100 text-green-700"
                    : "bg-rose-100 text-rose-700"
                }`}
              >
                {isWon ? (
                  <Trophy className="size-3" />
                ) : (
                  <XCircle className="size-3" />
                )}
                {isWon ? "Won" : "Lost"}
              </div>
            )}
            {!isWon && !isLost && <div className="h-[22px]" />}
          </div>

          {/* Action Icons */}
          <div className="flex items-center gap-3">
            <button
              title="Add Comment"
              className="p-2 rounded-full bg-slate-100 hover:bg-blue-100 hover:text-blue-600 text-slate-500 transition"
            >
              <MessageSquare className="size-4" />
            </button>

            <button
              onClick={() => !isWonOrLost && handleStageChange("Won")}
              title="Mark as Won"
              className={`p-2 rounded-full transition ${
                isWon
                  ? "bg-green-500 text-white"
                  : "bg-slate-100 text-slate-500 hover:bg-green-100 hover:text-green-600"
              }`}
            >
              <ThumbsUp className="size-4" />
            </button>

            <button
              onClick={() => !isWonOrLost && handleStageChange("Lost")}
              title="Mark as Lost"
              className={`p-2 rounded-full transition ${
                isLost
                  ? "bg-rose-500 text-white"
                  : "bg-slate-100 text-slate-500 hover:bg-rose-100 hover:text-rose-600"
              }`}
            >
              <ThumbsDown className="size-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Won Deal Modal */}
      {showWonModal && (
        <WonDeal
          wonFormDeal={wonFormDeal}
          onClose={() => setShowWonModal(false)}
          onDealUpdated={() => onStatusChange(deal.dealid)}
          onSubmit={handleSuccess}
          onError={handleError}
        />
      )}

      {showLostModal && (
        <LostDeal
          lostFormDeal={lostFormDeal}
          onClose={() => setShowLostModal(false)}
          onSubmit={handleSuccess}
          onError={handleError}
        />
      )}
    </>
  );
}

function TabPlaceholder({ icon: Icon, title, message }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-slate-300 rounded-2xl bg-white">
      <div className="flex items-center justify-center size-14 rounded-full bg-slate-100 mb-4">
        <Icon className="size-6 text-slate-500" />
      </div>
      <h3 className="text-lg font-semibold text-slate-700">{title}</h3>
      <p className="text-sm text-slate-500 mt-1">{message}</p>
      <div className="mt-4 px-4 py-2 text-xs text-slate-400 border border-solid border-slate-200 rounded-full bg-slate-50">
        🚧 Under Development
      </div>
    </div>
  );
}
